/**
 * Chat page component with multi-agent support.
 *
 * Features:
 * - Persistent collapsible left sidebar (conversations)
 * - Unified right panel with tabbed navigation
 * - Custom agent selector dropdown
 * - Simplified header
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryState, parseAsBoolean } from 'nuqs';
import {
  LogOut,
  Menu,
  PanelRightOpen,
  Sun,
  Moon,
} from 'lucide-react';
import { ChatWindow, ConversationList, DebugSidebar, AgentSelector, UsageSidebar, CxsMcpSettings } from '../components';
import { VoiceProvider, useVoice } from '../context/VoiceContext';
import { useCxsMcp } from '../context/CxsMcpContext';
import { useAgent } from '../context/AgentContext';
import { FilesSidebar } from '../components/files/FilesSidebar';
import { PlanSidebar } from '../components/plan/PlanSidebar';
import { CanvasSidebar } from '../components/canvas';
import { ToolsPanel } from '../components/tools/ToolsPanel';
import { SystemPromptSidebar } from '../components/prompt/SystemPromptSidebar';
import type { PromptSection, PromptInjection } from '../components/prompt/SystemPromptSidebar';
import { RightPanel, type PanelId } from '../components/panels/RightPanel';
import { useConversations, useChatWithWebSocket, useCanvas } from '../hooks';
import { useCanvasTools } from '../hooks/useCanvasTools';
import { useFileUpload, type UploadState, type UploadResult } from '../hooks/useFileUpload';
import { useFileList } from '../hooks/useFileList';
import { renderUploadNotification } from '../prompts/file-upload-notification';
import {
  usePlanStatus,
  extractPlanStatusFromEvent,
} from '../hooks/usePlanStatus';
import { fetchPlanStatusForSession } from '../api/fetchPlanStatus';
import { orchestratorApi } from '../services/orchestratorApi';
import { getAuthToken } from '../context/AuthContext';
import type { User } from '../hooks/useAuth';
import type { PlanStatusEvent, FileEvent, ToolsAvailableEvent, SystemPromptEvent, SystemPromptInjectionsEvent } from '../hooks/useChatWithWebSocket';

const SIDEBAR_COLLAPSED_KEY = 'cxs:sidebar:collapsed';
const DARK_MODE_KEY = 'cxs:darkmode';

/** Build a ComponentTree from a pushed canvas component descriptor. */
function buildCanvasTree(
  method: string,
  params: Record<string, unknown>,
): import('../types/canvas').ComponentTree | null {
  switch (method) {
    case 'render_card': {
      const children: import('../types/canvas').ComponentTree[] = [];

      if (params.content) {
        children.push({
          type: 'Text' as const,
          props: { content: params.content, format: 'markdown' },
        });
      }

      // Convert figures to Image children, deduplicated by src
      const figures = params.figures as Array<{ src: string; alt?: string }> | undefined;
      if (figures && Array.isArray(figures)) {
        const seenSrcs = new Set<string>();
        for (const fig of figures) {
          if (!fig || typeof fig !== 'object' || typeof fig.src !== 'string') {
            continue;
          }
          if (fig.src && !seenSrcs.has(fig.src)) {
            seenSrcs.add(fig.src);
            children.push({
              type: 'Image' as const,
              props: { src: fig.src, alt: fig.alt || '' },
            });
          }
        }
      }

      return {
        type: 'Card',
        props: {
          title: params.title || '',
          subtitle: params.subtitle || '',
          variant: params.variant || 'default',
        },
        ...(children.length > 0 ? { children } : {}),
      };
    }
    case 'render_alert':
      return {
        type: 'Alert',
        props: {
          message: params.message || '',
          severity: params.severity || 'info',
          title: params.title || '',
          dismissible: params.dismissible !== false,
        },
      };
    case 'render_table': {
      const columns = (params.columns as Array<unknown>)?.map((col) => {
        if (typeof col === 'string') return { key: col.toLowerCase().replace(/\s+/g, '_'), header: col };
        const obj = col as Record<string, string>;
        return { key: obj.key || '', header: obj.header || obj.key || '' };
      }) || [];
      const rows = (params.rows as Array<unknown>)?.map((row) => {
        if (Array.isArray(row)) {
          const record: Record<string, unknown> = {};
          columns.forEach((col, i) => { record[col.key] = i < row.length ? row[i] : ''; });
          return record;
        }
        return row as Record<string, unknown>;
      }) || [];
      const titleChild = params.title
        ? [{ type: 'Text' as const, props: { content: `## ${params.title}`, variant: 'heading', format: 'markdown' } }]
        : [];
      const tableTree = { type: 'Table' as const, props: { columns, rows, sortable: params.sortable !== false, pageSize: (params.page_size as number) || 10 } };
      return titleChild.length > 0
        ? { type: 'Card', props: {}, children: [...titleChild, tableTree] }
        : tableTree;
    }
    case 'render_image':
      return {
        type: 'Image',
        props: {
          src: params.src || '',
          alt: params.alt || '',
          width: params.width || '100%',
          height: params.height || 'auto',
          objectFit: params.object_fit || 'contain',
        },
      };
    case 'render_chart':
      return {
        type: 'Chart',
        props: {
          type: params.chart_type || 'bar',
          data: params.data || [],
          xKey: params.x_key || '',
          yKey: params.y_key || '',
          title: params.title || '',
          xLabel: params.x_label || '',
          yLabel: params.y_label || '',
          ...(params.colors ? { colors: params.colors } : {}),
        },
      };
    case 'render_text':
      return {
        type: 'Text',
        props: {
          content: params.content || '',
          variant: params.variant || 'body',
          format: params.format || 'markdown',
        },
      };
    default:
      return null;
  }
}

interface ChatPageProps {
  user: User;
  onLogout: () => void;
}

export function ChatPage(props: ChatPageProps) {
  return (
    <VoiceProvider>
      <ChatPageInner {...props} />
    </VoiceProvider>
  );
}

function ChatPageInner({ user, onLogout }: ChatPageProps) {
  const { conversationId: urlConversationId } = useParams<{
    conversationId?: string;
  }>();
  const navigate = useNavigate();

  const [debugOpen] = useQueryState('debug', parseAsBoolean.withDefault(false));

  const [urlAgent] = useQueryState('agent');
  const [urlCxsUserId] = useQueryState('cxsUserId');
  const [urlCxsOrgId] = useQueryState('cxsOrgId');
  const [urlServiceId] = useQueryState('serviceId');
  const [urlSessionToken] = useQueryState('sessionToken');

  // ─── Left sidebar state ───────────────────────────────────────────────────
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // ─── Dark mode ────────────────────────────────────────────────────────────
  const [isDark, setIsDark] = useState(() => {
    try {
      return localStorage.getItem(DARK_MODE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try {
      localStorage.setItem(DARK_MODE_KEY, String(isDark));
    } catch {
      // ignore
    }
  }, [isDark]);

  const toggleDark = useCallback(() => setIsDark((d) => !d), []);

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // ─── Right panel state (unified, replaces 7 booleans) ────────────────────
  const [activePanel, setActivePanel] = useState<PanelId | null>(null);
  // Remember last active panel so the header toggle reopens it
  const lastPanelRef = useRef<PanelId>('files');
  const handlePanelChange = useCallback((tab: PanelId | null) => {
    if (tab) lastPanelRef.current = tab;
    setActivePanel(tab);
  }, []);

  const togglePanel = useCallback((tab: PanelId) => {
    setActivePanel((prev) => {
      if (prev === tab) {
        return null; // close if already active
      }
      lastPanelRef.current = tab;
      return tab;
    });
  }, []);

  // Debug panel synced from URL query param
  useEffect(() => {
    if (debugOpen) {
      setActivePanel('debug');
      lastPanelRef.current = 'debug';
    }
  }, [debugOpen]);

  const [fileListRefreshKey, setFileListRefreshKey] = useState(0);
  const [systemPromptData, setSystemPromptData] = useState<{
    sections: PromptSection[];
    hasCxsContext: boolean;
    serviceId: string | null;
  }>({ sections: [], hasCxsContext: false, serviceId: null });
  const [systemPromptInjections, setSystemPromptInjections] = useState<PromptInjection[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const cxsMcp = useCxsMcp();
  const agent = useAgent();

  // Mystique persona lives in AgentContext: it is part of the URL slug
  // (`mystique_{persona}`) used by both the SSE chat route and the WebSocket.
  const { mystiqueSlug, setMystiqueSlug } = agent;
  const isMystique = agent.selectedAgent?.slug === 'mystique';

  const {
    conversations,
    activeConversation,
    loading: conversationsLoading,
    fetchConversations,
    loadConversation,
    createConversation,
  } = useConversations(user.id);

  const {
    strategy: planStrategy,
    status: planStatus,
    isActive: isPlanActive,
    isCollapsed: isPlanCollapsed,
    setStatus: setPlanStatus,
    clearStatus: clearPlanStatus,
    toggleCollapsed: togglePlanCollapsed,
  } = usePlanStatus();

  const canvas = useCanvas({
    sessionId: activeConversation?.session_id ?? null,
  });

  useCanvasTools(canvas);

  const handlePlanStatusUpdate = useCallback(
    (event: PlanStatusEvent) => {
      const extracted = extractPlanStatusFromEvent(event);
      if (extracted) {
        setPlanStatus(extracted.strategy, extracted.status, extracted.eventType);
        setActivePanel('plan');
        lastPanelRef.current = 'plan';
      }
    },
    [setPlanStatus],
  );

  const handleFileEvent = useCallback((_event: FileEvent) => {
    setFileListRefreshKey((prev) => prev + 1);
  }, []);

  const handleToolsAvailable = useCallback(
    (event: ToolsAvailableEvent) => {
      cxsMcp.setConnectedTools(event.tools);
      cxsMcp.setMcpStatus('connected');
    },
    [cxsMcp.setConnectedTools, cxsMcp.setMcpStatus],
  );

  const handleSystemPrompt = useCallback((event: SystemPromptEvent) => {
    setSystemPromptData(event);
    setSystemPromptInjections([]);
  }, []);

  const handleSystemPromptInjections = useCallback(
    (event: SystemPromptInjectionsEvent) => {
      setSystemPromptInjections(event.injections);
    },
    [],
  );

  const chat = useChatWithWebSocket({
    sessionId: activeConversation?.session_id ?? null,
    userId: user.id != null ? String(user.id) : null,
    organizationId: user.org_id != null ? String(user.org_id) : undefined,
    onPlanStatusUpdate: handlePlanStatusUpdate,
    onFileEvent: handleFileEvent,
    onCanvasUpdate: canvas.handleUpdate,
    onCanvasComplete: canvas.handleComplete,
    onCanvasError: canvas.handleError,
    onToolsAvailable: handleToolsAvailable,
    onSystemPrompt: handleSystemPrompt,
    onSystemPromptInjections: handleSystemPromptInjections,
    initialMessages: activeConversation?.messages,
    useWebSocket: true,
  });

  const prevChatStatusRef = useRef(chat.status);
  useEffect(() => {
    const prev = prevChatStatusRef.current;
    prevChatStatusRef.current = chat.status;
    if (
      (prev === 'streaming' || prev === 'submitted') &&
      chat.status === 'ready'
    ) {
      setFileListRefreshKey((k) => k + 1);
    }
  }, [chat.status]);

  const voice = useVoice();

  useEffect(() => {
    if (!voice.isVoiceActive || !chat.handleVoiceEvent) return;
    const handler = chat.handleVoiceEvent;
    return voice.onVoiceEvent((event) => handler(event as { type: string; [key: string]: unknown }));
  }, [voice.isVoiceActive, voice.onVoiceEvent, chat.handleVoiceEvent]);

  const sessionId = activeConversation?.session_id || '';
  const userIdStr = user.id != null ? String(user.id) : '';

  // Spec 056: hydrate the Plan panel from the orchestrator's tasks_context
  // projection when a conversation loads; SSE planStatus events stay
  // authoritative while streaming.
  useEffect(() => {
    clearPlanStatus();
    if (!sessionId || !userIdStr) return;
    let cancelled = false;
    fetchPlanStatusForSession(sessionId, userIdStr, getAuthToken)
      .then((result) => {
        if (!cancelled && result) {
          setPlanStatus(result.strategy, result.status);
        }
      })
      .catch(() => {
        /* hydration is best-effort; SSE remains the live source */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, userIdStr, setPlanStatus, clearPlanStatus]);

  // Show a welcome greeting once, before the user sends a first message in a
  // brand-new (still-empty) conversation. Fetched for every agent — the
  // backend decides whether one exists (currently Mystique personas only)
  // and returns null otherwise, so no per-agent gating is needed here.
  //
  // Gate on activeConversation.messages (not chat.messages): chat.messages
  // is derived state that useChat resets via its own effect on the same
  // sessionId change this effect reacts to, so reading it here can observe
  // a stale pre-reset value (e.g. leftover messages from the conversation
  // you just navigated away from) and wrongly skip the fetch forever, since
  // the session-id guard below prevents ever retrying for this session.
  const [welcomeMessageLoading, setWelcomeMessageLoading] = useState(false);
  const welcomeMessageSessionRef = useRef<string | null>(null);
  useEffect(() => {
    const agentSlug = agent.selectedAgent?.slug;
    if (!agentSlug || !sessionId) return;
    if ((activeConversation?.messages?.length ?? 0) > 0) return;
    if (welcomeMessageSessionRef.current === sessionId) return;
    welcomeMessageSessionRef.current = sessionId;

    let cancelled = false;
    setWelcomeMessageLoading(true);
    orchestratorApi
      .getWelcomeMessage(agentSlug, mystiqueSlug.trim() || undefined)
      .then((result) => {
        if (!cancelled && result.welcome_message) {
          chat.injectAssistantMessage(result.welcome_message);
        }
      })
      .catch(() => {
        /* welcome message is best-effort — chat works fine without it */
      })
      .finally(() => {
        if (!cancelled) setWelcomeMessageLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, agent.selectedAgent?.slug, mystiqueSlug]);

  // Refs to avoid stale closures in onBatchComplete polling loop
  const sendMessageRef = useRef(chat.sendMessage);
  sendMessageRef.current = chat.sendMessage;
  const chatStatusRef = useRef(chat.status);
  chatStatusRef.current = chat.status;
  /** Message to send once session_id exists (welcome create, deep-link load, or useChat not ready yet). */
  const pendingFirstUserMessageRef = useRef<string | null>(null);

  const fileUpload = useFileUpload({
    sessionId,
    userId: userIdStr,
    onUploadComplete: useCallback(
      (upload: UploadState) => {
        chat.addDebugEntry({
          type: 'stream-event',
          content: {
            eventType: 'file-upload-complete',
            payload: {
              type: 'file-upload-complete',
              filename: upload.filename,
              status: upload.status,
              description: upload.description,
              error: upload.error,
            },
            category: 'file',
          },
        });
      },
      [chat.addDebugEntry],
    ),
    onBatchComplete: useCallback(
      (results: UploadResult[]) => {
        if (results.length === 0) return;

        const message = renderUploadNotification(results);
        const MAX_RETRIES = 20; // 20 * 500ms = 10s max wait
        let attempts = 0;

        const trySend = () => {
          if (chatStatusRef.current === 'ready') {
            sendMessageRef.current(message);
          } else if (attempts < MAX_RETRIES) {
            attempts++;
            setTimeout(trySend, 500);
          }
        };
        trySend();
      },
      [],
    ),
  });

  const {
    files: workspaceFiles,
    refreshFiles: refreshWorkspaceFiles,
    loading: workspaceFilesLoading,
    error: workspaceFilesError,
  } = useFileList({
    sessionId,
    userId: userIdStr,
    autoFetch: !!sessionId && !!userIdStr,
  });

  useEffect(() => {
    if (fileListRefreshKey > 0) {
      refreshWorkspaceFiles();
    }
  }, [fileListRefreshKey, refreshWorkspaceFiles]);

  useEffect(() => {
    const pending = pendingFirstUserMessageRef.current;
    if (!pending || !activeConversation?.session_id) return;
    pendingFirstUserMessageRef.current = null;
    sendMessageRef.current(pending);
  }, [activeConversation?.session_id]);

  // Deep link failed (redirect to /chat) — drop queued text so it does not flush into a new chat later
  useEffect(() => {
    if (!urlConversationId && !activeConversation) {
      pendingFirstUserMessageRef.current = null;
    }
  }, [urlConversationId, activeConversation]);

  const urlParamsAppliedRef = useRef(false);
  const hasCxsUrlParams = !!(urlCxsUserId || urlCxsOrgId || urlServiceId);
  useEffect(() => {
    if (urlParamsAppliedRef.current) return;
    urlParamsAppliedRef.current = true;
    if (urlCxsUserId) cxsMcp.setCxsUserId(urlCxsUserId);
    if (urlCxsOrgId) cxsMcp.setCxsOrganizationId(urlCxsOrgId);
    if (urlServiceId) cxsMcp.setServiceId(urlServiceId);
    if (urlSessionToken) cxsMcp.setSessionToken(urlSessionToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mcpAutoConnectedRef = useRef(false);
  const agentReady = !urlAgent || agent.selectedAgent?.slug === urlAgent;
  useEffect(() => {
    if (
      hasCxsUrlParams &&
      !mcpAutoConnectedRef.current &&
      isInitialized &&
      agentReady &&
      activeConversation?.session_id &&
      cxsMcp.mcpStatus === 'idle'
    ) {
      mcpAutoConnectedRef.current = true;
      cxsMcp.setMcpStatus('connecting');
      chat.sendMessage('<system-hint>Initialize CXS MCP tools connection.</system-hint>');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCxsUrlParams, isInitialized, agentReady, activeConversation?.session_id, cxsMcp.mcpStatus]);

  useEffect(() => {
    if (urlAgent && agent.agents.length > 0 && agent.selectedAgent?.slug !== urlAgent) {
      agent.selectAgent(urlAgent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlAgent, agent.agents.length]);

  useEffect(() => {
    const initialize = async () => {
      await fetchConversations();
      setIsInitialized(true);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link and in-app navigation: load by id. Do NOT require the id to appear in
  // listConversations() — the index can be missing or stale while GET /conversations/:id works.
  useEffect(() => {
    if (!urlConversationId) return;
    let cancelled = false;
    void (async () => {
      const ok = await loadConversation(urlConversationId);
      if (!cancelled && !ok) {
        navigate('/chat', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlConversationId, loadConversation, navigate]);

  const handleSelectConversation = useCallback((id: string) => {
    navigate(`/chat/${id}`);
  }, [navigate]);

  const handleNewConversation = async () => {
    const convTitle = agent.selectedAgent
      ? `Chat with ${agent.selectedAgent.name}`
      : undefined;
    const newConv = await createConversation(convTitle);
    if (newConv) {
      navigate(`/chat/${newConv.id}`);
    }
  };

  // Spec 052 Step 9: voice-first / WSS-only bootstrap. Called by
  // VoiceButton when the user clicks Start before any conversation
  // exists. Mints a conversation, navigates to it, and returns the
  // new id so the voice WS can open against it immediately. The
  // route swap happens in parallel — the voice transport doesn't
  // need ``activeConversation`` to be loaded.
  const handleCreateVoiceSession = useCallback(async (): Promise<string | null> => {
    const convTitle = agent.selectedAgent
      ? `Chat with ${agent.selectedAgent.name}`
      : undefined;
    const newConv = await createConversation(convTitle);
    if (!newConv) return null;
    navigate(`/chat/${newConv.id}`, { replace: true });
    return newConv.id;
  }, [agent.selectedAgent, createConversation, navigate]);

  const handleSubmit = useCallback(
    async (text: string) => {
      // /chat/:id while loadConversation is still in flight — must not run
      // createConversation (that was opening a duplicate chat / losing the URL).
      if (!activeConversation && urlConversationId) {
        pendingFirstUserMessageRef.current = text;
        return;
      }
      // Welcome /chat — create conversation on first message
      if (!activeConversation) {
        const convTitle = agent.selectedAgent
          ? `Chat with ${agent.selectedAgent.name}`
          : undefined;
        const newConv = await createConversation(convTitle);
        if (newConv) {
          pendingFirstUserMessageRef.current = text;
          navigate(`/chat/${newConv.id}`, { replace: true });
          return;
        }
      }
      sendMessageRef.current(text);
    },
    [
      activeConversation,
      urlConversationId,
      agent.selectedAgent,
      createConversation,
      navigate,
    ],
  );

  const loading = conversationsLoading || chat.status === 'submitted';

  // Canvas tab visibility
  const canvasHidden = canvas.canvasCount === 0;
  // Prompt tab visibility (hidden when no sections loaded yet)
  const promptHidden = systemPromptData.sections.length === 0;

  // Auto-open canvas panel when first component arrives
  const prevCanvasCountRef = useRef(0);
  useEffect(() => {
    if (canvas.canvasCount > 0 && prevCanvasCountRef.current === 0) {
      setActivePanel('canvas');
      lastPanelRef.current = 'canvas';
    }
    prevCanvasCountRef.current = canvas.canvasCount;
  }, [canvas.canvasCount]);

  // Proactive training/kdense pushes include canvas components — switch to Canvas tab
  useEffect(() => {
    const onFocusCanvas = () => {
      setActivePanel('canvas');
      lastPanelRef.current = 'canvas';
    };
    window.addEventListener('cxs-focus-canvas', onFocusCanvas);
    return () => window.removeEventListener('cxs-focus-canvas', onFocusCanvas);
  }, []);

  // Handle canvas components pushed via agentPush
  useEffect(() => {
    const handler = (event: Event) => {
      const components = (event as CustomEvent).detail as Array<Record<string, unknown>>;
      if (!components) return;

      for (let i = 0; i < components.length; i++) {
        const comp = components[i];
        const method = comp.method as string;
        const componentId = (comp.component_id as string) || `push-${Date.now()}-${i}`;
        const canvasId = (comp.canvas_id as string) || 'main';
        const artifactId = `push-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`;

        if (method === 'remove_component') {
          canvas.handleUpdate({
            canvasId,
            componentId,
            artifactId,
            action: 'remove',
          });
          canvas.handleComplete({
            canvasId,
            componentId,
            artifactId,
            action: 'remove',
          });
          continue;
        }

        // Build ComponentTree from the push data
        const tree = buildCanvasTree(method, comp);
        if (!tree) continue;

        canvas.handleUpdate({
          canvasId,
          componentId,
          artifactId,
          jsonTree: tree,
          action: 'upsert',
        });
        canvas.handleComplete({
          canvasId,
          componentId,
          artifactId,
          jsonTree: tree,
          action: 'upsert',
        });
      }
    };
    window.addEventListener('cxs-canvas-push', handler);
    return () => window.removeEventListener('cxs-canvas-push', handler);
  }, [canvas.handleUpdate, canvas.handleComplete]);

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-950 flex flex-col overflow-hidden">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-sm border-b border-gray-200/70 dark:border-gray-700/70 px-3 flex items-center justify-between relative z-20 flex-shrink-0" style={{ height: '52px' }}>
        {/* Left: mobile hamburger (shows mobile sidebar overlay) */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open sidebar"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          {/* Agent selector */}
          <AgentSelector />

          {/* Mystique slug input — only shown when Mystique is the selected agent */}
          {isMystique && (
            <div className="flex items-center gap-1.5 ml-1">
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium hidden sm:block">
                slug
              </span>
              <input
                type="text"
                value={mystiqueSlug}
                onChange={(e) => setMystiqueSlug(e.target.value)}
                placeholder="ssp-agent-slug"
                className="h-7 px-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-sky-400 dark:focus:ring-sky-600 w-40"
                aria-label="Mystique SSP agent slug"
              />
            </div>
          )}
        </div>

        {/* Right: MCP settings + panel toggle + logout */}
        <div className="flex items-center gap-1">
          <CxsMcpSettings />

          {/* Single panel toggle button */}
          <button
            onClick={() => {
              if (activePanel) {
                setActivePanel(null);
              } else {
                handlePanelChange(lastPanelRef.current);
              }
            }}
            className={`p-2 rounded-lg transition-colors ${
              activePanel
                ? 'bg-sky-50 text-sky-600'
                : 'hover:bg-gray-100 text-gray-500'
            }`}
            title={activePanel ? 'Close panel' : 'Open panel'}
            data-testid="toggle-right-panel"
          >
            <PanelRightOpen className="w-5 h-5" />
          </button>

          {/* Dark mode toggle */}
          <button
            onClick={toggleDark}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="dark-mode-toggle"
          >
            {isDark ? (
              <Sun className="w-5 h-5 text-amber-400" />
            ) : (
              <Moon className="w-5 h-5 text-gray-500" />
            )}
          </button>

          <span
            className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-300"
            title={user.name}
          >
            {user.name.charAt(0).toUpperCase()}
          </span>
          <button
            onClick={onLogout}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Logout"
          >
            <LogOut className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </header>

      {/* ─── Body ────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left sidebar */}
        <ConversationList
          conversations={conversations}
          activeId={activeConversation?.id}
          currentUser={user}
          loading={loading}
          onSelect={handleSelectConversation}
          onNew={handleNewConversation}
          isCollapsed={isSidebarCollapsed}
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={() => setIsMobileSidebarOpen(false)}
          onToggleCollapse={toggleSidebarCollapsed}
        />

        {/* Chat window */}
        <ChatWindow
          title={agent.selectedAgent?.name}
          messages={chat.messages}
          status={chat.status}
          onSubmit={handleSubmit}
          onStop={chat.stop}
          onRetry={chat.retry}
          onRegenerate={chat.reload}
          planStrategy={planStrategy}
          planStatus={planStatus}
          isPlanActive={isPlanActive}
          onOpenPlanPanel={() => togglePanel('plan')}
          sessionId={sessionId}
          userId={userIdStr}
          onCreateSession={handleCreateVoiceSession}
          fileUpload={fileUpload}
          workspaceFiles={workspaceFiles}
          filesSidebarOpen={activePanel === 'files'}
          toolMetadata={chat.toolMetadata}
          lastPushTimestamp={chat.lastPushTimestamp}
          sendErrorDetail={chat.error?.message ?? null}
          welcomeMessageLoading={welcomeMessageLoading}
        />

        {/* Right panel — unified tabbed container */}
        <RightPanel
          activeTab={activePanel}
          onTabChange={handlePanelChange}
          toolsBadge={cxsMcp.connectedTools.length}
          canvasHidden={canvasHidden}
          promptHidden={promptHidden}
        >
          <FilesSidebar
            sessionId={sessionId}
            userId={userIdStr}
            uploads={fileUpload.uploads}
            onCancelUpload={fileUpload.cancelUpload}
            onCancelAllUploads={fileUpload.cancelAll}
            onClearCompletedUploads={fileUpload.clearCompleted}
            isUploading={fileUpload.isUploading}
            totalProgress={fileUpload.totalProgress}
            isOpen={activePanel === 'files'}
            onClose={() => handlePanelChange(null)}
            refreshKey={fileListRefreshKey}
            onAddFiles={fileUpload.addFiles}
            files={workspaceFiles}
            onRefreshFiles={refreshWorkspaceFiles}
            filesLoading={workspaceFilesLoading}
            filesError={workspaceFilesError}
          />
          <PlanSidebar
            isOpen={activePanel === 'plan'}
            onClose={() => handlePanelChange(null)}
            strategy={planStrategy}
            status={planStatus}
            isPlanActive={isPlanActive}
            isCollapsed={isPlanCollapsed}
            onToggleCollapsed={togglePlanCollapsed}
          />
          <CanvasSidebar
            isOpen={activePanel === 'canvas'}
            canvases={Array.from(canvas.canvases.values())}
            activeCanvasId={canvas.activeCanvasId}
            onClose={() => handlePanelChange(null)}
            onSelectCanvas={canvas.setActiveCanvas}
            onCloseCanvas={canvas.closeCanvas}
            width={canvas.panelWidth}
            onWidthChange={canvas.setPanelWidth}
          />
          <ToolsPanel
            isOpen={activePanel === 'tools'}
            onClose={() => handlePanelChange(null)}
            tools={cxsMcp.connectedTools}
          />
          <SystemPromptSidebar
            isOpen={activePanel === 'prompt'}
            onClose={() => handlePanelChange(null)}
            sections={systemPromptData.sections}
            hasCxsContext={systemPromptData.hasCxsContext}
            serviceId={systemPromptData.serviceId}
            injections={systemPromptInjections}
          />
          <DebugSidebar
            isOpen={activePanel === 'debug'}
            entries={chat.debugEntries}
            onClose={() => handlePanelChange(null)}
          />
          <UsageSidebar
            isOpen={activePanel === 'usage'}
            onClose={() => handlePanelChange(null)}
            messages={chat.messages}
            toolTimings={chat.toolTimings}
          />
        </RightPanel>
      </div>
    </div>
  );
}
